/**
 * Reusable Spanish service-page template.
 *
 * Instead of duplicating 24 near-identical service pages, we define content
 * once as a data object per service and render through this template.
 * Achieves the brief's intent (native Spanish copy, dental glossary, JSON-LD
 * mirroring, hreflang loop) with a fraction of the JSX footprint.
 *
 * Every content object passed in MUST have already been proofed against the
 * dental glossary in FORTWORTHDENTAL_BILINGUAL_BRIEF.md.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import EsSEO from '@/components/seo/EsSEO';
import BilingualForm from '@/components/BilingualForm';
import useSpanishTrackingNumber from '@/hooks/useSpanishTrackingNumber';

const SITE = 'https://fortworthdental.com';
const SCHEMA_PHONE = '(817) 985-3337';

const buildSchema = (c) => ({
  '@context': 'https://schema.org',
  '@type': 'MedicalProcedure',
  name: c.schemaName,
  description: c.description,
  url: `${SITE}${c.esPath}`,
  procedureType: 'http://schema.org/NoninvasiveProcedure',
  inLanguage: 'es-US',
  provider: {
    '@type': 'Dentist',
    name: 'Fort Worth Dental',
    telephone: SCHEMA_PHONE,
    url: SITE,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '4620 Citylake Blvd W',
      addressLocality: 'Fort Worth',
      addressRegion: 'TX',
      postalCode: '76132',
      addressCountry: 'US',
    },
  },
  areaServed: ['Fort Worth', 'Arlington', 'Benbrook', 'Forest Hill', 'White Settlement', 'Westover Hills'].map((n) => ({
    '@type': 'City',
    name: n,
  })),
});

const breadcrumbSchema = (c) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  inLanguage: 'es-US',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Inicio', item: `${SITE}/es` },
    { '@type': 'ListItem', position: 2, name: 'Servicios', item: `${SITE}/es/servicios` },
    { '@type': 'ListItem', position: 3, name: c.h1, item: `${SITE}${c.esPath}` },
  ],
});

const SpanishServiceTemplate = ({ content }) => {
  const c = content;
  const { displayNumber, telLink } = useSpanishTrackingNumber();
  return (
    <>
      <EsSEO
        title={c.metaTitle}
        description={c.metaDescription}
        canonical={c.esPath}
        hreflangEn={c.enPath}
        ogImage={c.ogImage || `${SITE}/logo-fwd.png`}
        schema={{ '@graph': [buildSchema(c), breadcrumbSchema(c)] }}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-teal-700 via-teal-600 to-emerald-600 text-white py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <nav aria-label="breadcrumb" className="text-sm text-teal-100 mb-4">
            <Link to="/es" className="hover:text-white">Inicio</Link>
            <span className="mx-2">/</span>
            <Link to="/es/servicios" className="hover:text-white">Servicios</Link>
            <span className="mx-2">/</span>
            <span className="text-white">{c.h1}</span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 max-w-4xl">{c.h1}</h1>
          <p className="text-lg lg:text-xl text-teal-50 max-w-3xl">{c.subtitle}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={telLink}
              className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-6 py-3 rounded-md"
              data-testid={`es-${c.slug}-call`}
            >
              📞 Llamar al {displayNumber}
            </a>
            <Link
              to="/es/contacto"
              className="bg-white text-teal-700 hover:bg-teal-50 font-semibold px-6 py-3 rounded-md"
              data-testid={`es-${c.slug}-book`}
            >
              Reservar Consulta Gratis
            </Link>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            {c.image && (
              <img
                src={c.image}
                alt={c.imageAlt}
                className="rounded-lg shadow-lg w-full"
                loading="lazy"
              />
            )}
            <div className={c.image ? '' : 'md:col-span-2'}>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">{c.introHeadline}</h2>
              {c.introParagraphs.map((p, i) => (
                <p key={i} className="text-gray-700 leading-relaxed mb-4">{p}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      {c.benefits && c.benefits.length > 0 && (
        <section className="py-16 bg-teal-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">{c.benefitsHeadline}</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {c.benefits.map((b, i) => (
                <div key={i} className="bg-white rounded-lg p-5 shadow-sm border border-teal-100">
                  <p className="text-gray-800">
                    <span className="text-teal-600 font-bold mr-2">✓</span>
                    <strong>{b.title}:</strong> {b.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ (optional) */}
      {c.faq && c.faq.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Preguntas Frecuentes</h2>
            <div className="space-y-5">
              {c.faq.map((f, i) => (
                <details key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-5 group">
                  <summary className="font-semibold text-gray-900 cursor-pointer">{f.q}</summary>
                  <p className="mt-3 text-gray-700 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Conversion CTA + inline ES form */}
      <section className="py-16 bg-gradient-to-br from-amber-50 via-white to-teal-50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">{c.ctaHeadline}</h2>
              <p className="text-gray-700 mb-4">{c.ctaBody}</p>
              <ul className="text-gray-700 space-y-2 text-sm mb-6">
                <li>✓ Aceptamos seguro dental</li>
                <li>✓ Consulta inicial gratuita</li>
                <li>✓ Más de 1,100 reseñas de 5 estrellas</li>
                <li>✓ Hablamos español — atendemos a toda tu familia</li>
              </ul>
              <a
                href={telLink}
                className="inline-block bg-teal-600 hover:bg-teal-700 text-white font-semibold px-8 py-3 rounded-md"
              >
                Llamar Ahora: {displayNumber}
              </a>
            </div>
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-1">Solicita tu cita</h3>
              <p className="text-sm text-gray-600 mb-4">Te llamamos en menos de 1 día hábil.</p>
              <BilingualForm lang="es" source={`es_service_${c.slug}`} />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SpanishServiceTemplate;
